import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import logo from '../assets/logo.png';
import { SimulationData } from '../App';

interface HeroProps {
  onStartSimulation: (data: SimulationData) => void;
}

interface LeadFormData {
  country: string;
  city: string;
  email: string;
  [key: string]: string | number | undefined;
}

interface FormErrors {
  country?: string;
  city?: string;
  email?: string;
}

const Hero: React.FC<HeroProps> = ({ onStartSimulation }) => {
  const [formData, setFormData] = useState<LeadFormData>({ country: '', city: '', email: '' });
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    const lastCountry = localStorage.getItem('lastCountry');
    if (lastCountry) {
      setFormData(prev => ({ ...prev, country: lastCountry }));
    }
  }, []);

  const validate = (): FormErrors => {
    const newErrors: FormErrors = {};
    if (!formData.country) newErrors.country = 'Please select a country';
    if (!formData.city.trim()) newErrors.city = 'Please enter a city';
    if (!formData.email.trim()) newErrors.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    return newErrors;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});
    onStartSimulation(formData);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section className="hero">
      <div className="hero-inner">
        <div className="hero-content">
          <div className="mckinsey-badge">
            <div className="badge-icon">
              <img src={logo} alt="Market Entry Logo" className="badge-logo-img" />
            </div>
            <div className="badge-content">
              <span className="badge-main">Built by <strong>ex-McKinsey</strong> researchers</span>
            </div>
          </div>
          <div className="hero-label"><span>Completely Free · Delivered in 72 Hours</span></div>
          <h1>See how your expansion<br /><em>Plays out before you invest.</em></h1>
          <p className="hero-sub">We project your revenue, true costs, and break-even month by month, then compare your target market against multiple markets so you can see which ones offer faster profitability and lower risk.</p>
        </div>
        <div className="form-card">
          <h3>Get your free simulation</h3>
          <p className="form-sub">Tell us your target market. We deliver your market simulation within 72 hours.</p>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Target Country *</label>
                <select name="country" value={formData.country} onChange={handleChange} style={errors.country ? { borderColor: '#c0392b' } : {}}>
                  <option value="">Select a Country</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Spain">Spain</option>
                  <option value="Italy">Italy</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                  <option value="Japan">Japan</option>
                </select>
                {errors.country && <span style={{ fontSize: '11px', color: '#c0392b', marginTop: '4px', display: 'block' }}>{errors.country}</span>}
              </div>
              <div className="form-group">
                <label>Target City *</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="e.g., New York" style={errors.city ? { borderColor: '#c0392b' } : {}} />
                {errors.city && <span style={{ fontSize: '11px', color: '#c0392b', marginTop: '4px', display: 'block' }}>{errors.city}</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Your Work Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="e.g., you@company.com" style={errors.email ? { borderColor: '#c0392b' } : {}} />
              {errors.email && <span style={{ fontSize: '11px', color: '#c0392b', marginTop: '4px', display: 'block' }}>{errors.email}</span>}
            </div>
            <button type="submit" className="form-submit">Start My Simulation →</button>
            <div className="form-privacy">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <p><strong>Your Data Stays Private</strong><br />All information you share remains strictly confidential and is never shared without your explicit permission.</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Hero;
